import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import { useLocationContext } from '../contexts/LocationContext';
import { useEquipmentGroups } from '../utils/equipmentGroups';
import {
  getSOPs,
  createSOP,
  updateSOP,
  deleteSOP,
  getSOPRevisions,
  restoreSOPRevision,
  getArchivedSOPs,
  restoreArchivedSOP,
  getSOPsForLocation,
  importSOPs,
} from '../services/maintenanceService';
import { uploadPhoto, photoUrl } from '../services/photoService';

const DEFAULT_CATEGORIES = [
  'General',
  'Daily Tasks',
  'Weekly Tasks',
  'Testing',
  'Troubleshooting',
  'Repairs',
  'Setup',
  'Safety',
];

// Same set used by schedules and work orders — keeps the mental model
// consistent across the maintenance feature.
const MARKDOWN_COMPONENTS = {
  h1: ({ children }) => <h2 className="text-2xl font-bold mt-4 mb-2 text-gray-900">{children}</h2>,
  h2: ({ children }) => <h3 className="text-xl font-bold mt-4 mb-2 text-gray-900">{children}</h3>,
  h3: ({ children }) => <h4 className="text-lg font-semibold mt-3 mb-1 text-gray-900">{children}</h4>,
  p: ({ children }) => <p className="my-2 leading-relaxed text-gray-800">{children}</p>,
  ul: ({ children }) => <ul className="list-disc ml-6 my-2 space-y-1 text-gray-800">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-6 my-2 space-y-1 text-gray-800">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  code: ({ children }) => <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-pink-700">{children}</code>,
  pre: ({ children }) => <pre className="bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto my-2 text-sm">{children}</pre>,
  hr: () => <hr className="my-4 border-gray-300" />,
  a: ({ children, href }) => <a href={href} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700 my-2">{children}</blockquote>,
  img: ({ src, alt }) => (
    <img
      src={src && src.startsWith('/api/') ? photoUrl(src) : src}
      alt={alt || ''}
      loading="lazy"
      className="max-w-full h-auto rounded shadow-sm my-3"
    />
  ),
};

const PLACEHOLDER_BODY = `# Step-by-step procedure

1. First, do X
2. Then check Y
3. If Y looks like Z, run W

## Things to watch out for

- **Important:** never bypass the safety interlock
- If you hear a grinding noise, stop immediately

## Tools needed

- 10mm wrench
- Pressure gauge
- Clean rag`;

const MaintenanceSOPs = () => {
  const { membership, user } = useAuth();
  const { currentLocation, locations } = useLocationContext();
  const isAdmin = user?.is_superadmin || membership?.role === 'owner' || membership?.role === 'admin';
  // Other locations the admin can import from — same account, not the current one.
  const otherLocations = (locations || []).filter((l) => l.id !== currentLocation?.id);

  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const [viewing, setViewing] = useState(null);   // SOP being read
  const [editing, setEditing] = useState(null);   // SOP being edited (or {} for new)
  const [history, setHistory] = useState(null);   // { sop, revisions } being browsed
  const [archived, setArchived] = useState(null); // deleted SOPs available to restore
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const fetchSops = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getSOPs();
      setSops(data.sops || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load SOPs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation?.id]);

  // Filter + group by category
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sops.filter((s) => {
      if (activeCategory !== 'all' && s.category !== activeCategory) return false;
      if (!term) return true;
      return (
        (s.title || '').toLowerCase().includes(term) ||
        (s.body || '').toLowerCase().includes(term)
      );
    });
  }, [sops, search, activeCategory]);

  const grouped = useMemo(() => {
    const out = {};
    filtered.forEach((s) => {
      const cat = s.category || 'General';
      if (!out[cat]) out[cat] = [];
      out[cat].push(s);
    });
    return out;
  }, [filtered]);

  const categoriesInUse = useMemo(() => {
    const set = new Set(sops.map((s) => s.category || 'General'));
    return Array.from(set).sort();
  }, [sops]);

  const handleSave = async (payload) => {
    try {
      if (payload.id) {
        await updateSOP(payload.id, payload);
      } else {
        await createSOP(payload);
      }
      setEditing(null);
      fetchSops();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save SOP');
    }
  };

  const handleDelete = async (sop) => {
    // Deletes are archived server-side, so this is recoverable by an admin.
    if (!window.confirm(`Delete "${sop.title}"?\n\nIt will be kept in Deleted SOPs and an admin can restore it.`)) return;
    try {
      await deleteSOP(sop.id);
      setViewing(null);
      fetchSops();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete SOP');
    }
  };

  const openHistory = async (sop) => {
    setError('');
    try {
      setBusy(true);
      const data = await getSOPRevisions(sop.id);
      setHistory({ sop, revisions: data.revisions || [] });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load history');
    } finally {
      setBusy(false);
    }
  };

  const handleRestoreRevision = async (revision) => {
    if (!window.confirm('Restore this version? The current text is saved to history first, so this can be undone.')) return;
    try {
      setBusy(true);
      await restoreSOPRevision(history.sop.id, revision.id);
      setHistory(null);
      setViewing(null);
      fetchSops();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore version');
    } finally {
      setBusy(false);
    }
  };

  const openArchived = async () => {
    setError('');
    try {
      setBusy(true);
      const data = await getArchivedSOPs();
      setArchived(data.archived || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load deleted SOPs');
    } finally {
      setBusy(false);
    }
  };

  const handleRestoreArchived = async (revision) => {
    if (!window.confirm(`Restore "${revision.title}"?`)) return;
    try {
      setBusy(true);
      await restoreArchivedSOP(revision.id);
      setArchived(null);
      fetchSops();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore SOP');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <Link to="/maintenance" className="text-sm text-gray-500 hover:text-gray-700">← Maintenance</Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-1">Standard Operating Procedures</h1>
          <p className="text-sm text-gray-500 mt-1">How-to docs for testing, repairing, and running the wash.</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            {otherLocations.length > 0 && (
              <button
                onClick={() => setImportOpen(true)}
                className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium"
              >
                Import from another wash
              </button>
            )}
            <button
              onClick={openArchived}
              className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium"
            >
              Deleted SOPs
            </button>
            <button
              onClick={() => setEditing({ title: '', body: '', category: 'General', equipment_category: [] })}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              + New SOP
            </button>
          </div>
        )}
      </div>

      {/* Search + category filter */}
      <div className="bg-white rounded-lg shadow p-4 space-y-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title and body…"
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex flex-wrap gap-1.5">
          <CategoryChip
            label="All"
            active={activeCategory === 'all'}
            onClick={() => setActiveCategory('all')}
          />
          {categoriesInUse.map((c) => (
            <CategoryChip key={c} label={c} active={activeCategory === c} onClick={() => setActiveCategory(c)} />
          ))}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : sops.length === 0 ? (
        <EmptyState isAdmin={isAdmin} onCreate={() => setEditing({ title: '', body: '', category: 'General' })} />
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No SOPs match your filter.
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, items]) => (
            <section key={cat}>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2 px-1">{cat}</h2>
              <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
                {items.map((sop) => (
                  <button
                    key={sop.id}
                    onClick={() => setViewing(sop)}
                    className="w-full text-left p-4 hover:bg-gray-50 transition flex items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{sop.title}</h3>
                      {sop.body && (
                        <p className="text-sm text-gray-500 truncate mt-0.5">
                          {sop.body.replace(/[#*`>_~\-\[\]]/g, '').slice(0, 140)}
                        </p>
                      )}
                      {sop.equipment_category && sop.equipment_category.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {sop.equipment_category.map((c) => (
                            <span key={c} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {viewing && (
        <ViewModal
          sop={viewing}
          isAdmin={isAdmin}
          onClose={() => setViewing(null)}
          onEdit={() => { setEditing(viewing); setViewing(null); }}
          onDelete={() => handleDelete(viewing)}
          onHistory={() => openHistory(viewing)}
        />
      )}

      {history && (
        <HistoryModal
          sop={history.sop}
          revisions={history.revisions}
          isAdmin={isAdmin}
          busy={busy}
          onClose={() => setHistory(null)}
          onRestore={handleRestoreRevision}
        />
      )}

      {archived && (
        <ArchivedModal
          archived={archived}
          busy={busy}
          onClose={() => setArchived(null)}
          onRestore={handleRestoreArchived}
        />
      )}

      {editing && (

        <EditModal
          initial={editing}
          knownCategories={Array.from(new Set([...DEFAULT_CATEGORIES, ...categoriesInUse]))}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      {importOpen && (
        <ImportModal
          otherLocations={otherLocations}
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); fetchSops(); }}
        />
      )}
    </div>
  );
};

const CategoryChip = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 rounded-full text-xs font-medium transition ${
      active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}
  >
    {label}
  </button>
);

const EmptyState = ({ isAdmin, onCreate }) => (
  <div className="bg-white rounded-lg shadow p-12 text-center">
    <div className="w-16 h-16 mx-auto bg-blue-50 rounded-full flex items-center justify-center mb-4">
      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
    <h2 className="text-xl font-semibold text-gray-900">No SOPs yet</h2>
    <p className="mt-2 text-gray-600 max-w-md mx-auto">
      Document procedures here so staff can self-serve answers when something breaks or they need to test equipment.
    </p>
    {isAdmin && (
      <button
        onClick={onCreate}
        className="mt-6 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
      >
        + Write your first SOP
      </button>
    )}
  </div>
);

const ViewModal = ({ sop, isAdmin, onClose, onEdit, onDelete, onHistory }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-gray-500">{sop.category}</div>
          <h2 className="text-xl font-bold text-gray-900 mt-0.5">{sop.title}</h2>
          {sop.equipment_category && sop.equipment_category.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {sop.equipment_category.map((c) => (
                <span key={c} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{c}</span>
              ))}
            </div>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="px-6 py-4 overflow-y-auto flex-1">
        {sop.body ? (
          <ReactMarkdown components={MARKDOWN_COMPONENTS}>{sop.body}</ReactMarkdown>
        ) : (
          <p className="text-gray-400 italic">No content yet.</p>
        )}
      </div>
      <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center gap-2">
        <button
          onClick={onHistory}
          className="text-gray-600 hover:bg-gray-100 px-3 py-1.5 rounded text-sm font-medium"
        >
          History
        </button>
        {isAdmin && (
          <div className="flex gap-2">
          <button
            onClick={onDelete}
            className="text-red-600 hover:bg-red-50 px-3 py-1.5 rounded text-sm font-medium"
          >
            Delete
          </button>
          <button
            onClick={onEdit}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded text-sm font-medium"
          >
            Edit
          </button>
          </div>
        )}
      </div>
    </div>
  </div>
);

const fmtWhen = (iso) => {
  if (!iso) return 'Unknown time';
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return isNaN(d) ? 'Unknown time' : d.toLocaleString();
};

// Version history for a live SOP. Everyone can look; only admins can roll back.
const HistoryModal = ({ sop, revisions, isAdmin, busy, onClose, onRestore }) => {
  const [preview, setPreview] = useState(null);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-gray-500">Version history</div>
            <h2 className="text-xl font-bold text-gray-900 mt-0.5 truncate">{sop.title}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Close">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {revisions.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No earlier versions yet. Edits from now on are saved here automatically.
            </p>
          ) : preview ? (
            <div>
              <button
                onClick={() => setPreview(null)}
                className="text-sm text-blue-600 hover:text-blue-800 mb-3"
              >
                ← Back to list
              </button>
              <div className="text-xs text-gray-500 mb-2">
                Saved {fmtWhen(preview.created_at)} · replaced by {preview.editor_name}
              </div>
              <div className="border border-gray-200 rounded p-4 bg-gray-50">
                <div className="font-medium text-gray-900 mb-2">{preview.title}</div>
                {preview.body ? (
                  <ReactMarkdown components={MARKDOWN_COMPONENTS}>{preview.body}</ReactMarkdown>
                ) : (
                  <p className="text-gray-400 italic">Empty.</p>
                )}
              </div>
              {isAdmin && (
                <button
                  onClick={() => onRestore(preview)}
                  disabled={busy}
                  className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded text-sm font-medium disabled:opacity-50"
                >
                  Restore this version
                </button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {revisions.map((r) => (
                <li key={r.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{r.title}</div>
                    <div className="text-xs text-gray-500">
                      Version before {r.editor_name}'s edit · {fmtWhen(r.created_at)}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => setPreview(r)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      View
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => onRestore(r)}
                        disabled={busy}
                        className="text-gray-700 hover:text-gray-900 text-sm font-medium disabled:opacity-50"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// Deleted SOPs, restorable by an admin.
const ArchivedModal = ({ archived, busy, onClose, onRestore }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Deleted SOPs</h2>
          <p className="text-sm text-gray-500 mt-0.5">Restoring re-creates the SOP with its last saved content.</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Close">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="px-6 py-4 overflow-y-auto flex-1">
        {archived.length === 0 ? (
          <p className="text-gray-500 text-sm">Nothing deleted.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {archived.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{r.title}</div>
                  <div className="text-xs text-gray-500">
                    Deleted by {r.editor_name} · {fmtWhen(r.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => onRestore(r)}
                  disabled={busy}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium flex-shrink-0 disabled:opacity-50"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </div>
);

const EditModal = ({ initial, knownCategories, onCancel, onSave }) => {
  const equipmentGroups = useEquipmentGroups();
  const [form, setForm] = useState({
    id: initial.id,
    title: initial.title || '',
    category: initial.category || 'General',
    body: initial.body || '',
    equipment_category: Array.isArray(initial.equipment_category) ? initial.equipment_category : [],
  });
  const [tab, setTab] = useState('write'); // 'write' | 'preview'
  const [addingCategory, setAddingCategory] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const bodyRef = useRef(null);
  const photoInputRef = useRef(null);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const insertPhotoMarkdown = (photo, altSeed) => {
    const alt = (altSeed || 'photo').replace(/[\[\]]/g, '');
    const snippet = `\n\n![${alt}](${photo.url})\n\n`;
    const textarea = bodyRef.current;
    setForm((prev) => {
      const current = prev.body || '';
      if (textarea && textarea.selectionStart != null) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        return { ...prev, body: current.slice(0, start) + snippet + current.slice(end) };
      }
      return { ...prev, body: current + snippet };
    });
  };

  const handleInsertPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    setPhotoError('');
    try {
      const photo = await uploadPhoto(file, { contextType: 'sop', contextId: form.id || null });
      insertPhotoMarkdown(photo, file.name.replace(/\.[^.]+$/, ''));
    } catch (err) {
      setPhotoError(err.response?.data?.error || 'Photo upload failed');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const toggleEquipmentGroup = (value) => {
    setForm((prev) => {
      const cats = prev.equipment_category;
      return cats.includes(value)
        ? { ...prev, equipment_category: cats.filter((c) => c !== value) }
        : { ...prev, equipment_category: [...cats, value] };
    });
  };

  const submit = () => {
    if (!form.title.trim()) {
      alert('Title is required');
      return;
    }
    if (!form.category.trim()) {
      alert('Category is required');
      return;
    }
    onSave({
      ...form,
      title: form.title.trim(),
      category: form.category.trim(),
      equipment_category: form.equipment_category,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {form.id ? 'Edit SOP' : 'New SOP'}
          </h2>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. How to reset the foam brush pump"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            {addingCategory ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => set('category', e.target.value)}
                  placeholder="New category name"
                  autoFocus
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => { setAddingCategory(false); set('category', knownCategories[0] || 'General'); }}
                  className="text-sm text-gray-500 hover:text-gray-700 px-2"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <select
                value={knownCategories.includes(form.category) ? form.category : '__custom__'}
                onChange={(e) => {
                  if (e.target.value === '__add_new__') {
                    setAddingCategory(true);
                    set('category', '');
                  } else {
                    set('category', e.target.value);
                  }
                }}
                className="w-full sm:w-64 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {!knownCategories.includes(form.category) && form.category && (
                  <option value="__custom__">{form.category}</option>
                )}
                {knownCategories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
                <option value="__add_new__">+ Add new category…</option>
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Equipment Groups <span className="text-gray-400 font-normal">(optional)</span></label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {equipmentGroups.map((g) => {
                const active = form.equipment_category.includes(g.value);
                return (
                  <label
                    key={g.value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      active
                        ? 'bg-blue-50 border-blue-300 text-blue-800'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleEquipmentGroup(g.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium">{g.label}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Tag which equipment this procedure relates to. Leave empty for general SOPs.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
              <label className="block text-sm font-medium text-gray-700">Body</label>
              <div className="flex items-center gap-2 text-xs">
                <label className={`px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer ${photoUploading ? 'opacity-50 cursor-wait' : ''}`}>
                  {photoUploading ? 'Uploading…' : '+ Insert photo'}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleInsertPhoto}
                    disabled={photoUploading}
                    className="hidden"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setTab('write')}
                  className={`px-2 py-1 rounded ${tab === 'write' ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Write
                </button>
                <button
                  type="button"
                  onClick={() => setTab('preview')}
                  className={`px-2 py-1 rounded ${tab === 'preview' ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Preview
                </button>
              </div>
            </div>
            {photoError && (
              <div className="mb-2 text-xs text-red-600">{photoError}</div>
            )}
            {tab === 'write' ? (
              <textarea
                ref={bodyRef}
                value={form.body}
                onChange={(e) => set('body', e.target.value)}
                placeholder={PLACEHOLDER_BODY}
                rows={16}
                className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="border border-gray-200 rounded-md px-4 py-3 min-h-[24rem] bg-gray-50">
                {form.body.trim() ? (
                  <ReactMarkdown components={MARKDOWN_COMPONENTS}>{form.body}</ReactMarkdown>
                ) : (
                  <p className="text-gray-400 italic">Nothing to preview yet.</p>
                )}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1">
              Markdown supported. Use <code>#</code> for headings, <code>-</code> for bullets, <code>1.</code> for numbered steps, <code>**bold**</code>. Photos insert at your cursor position.
            </p>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium"
          >
            {form.id ? 'Save Changes' : 'Create SOP'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ImportModal = ({ otherLocations, onClose, onImported }) => {
  const [sourceId, setSourceId] = useState(otherLocations[0]?.id || '');
  const [sourceSops, setSourceSops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null); // {imported: [], skipped: []}
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sourceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setResult(null);
      try {
        const data = await getSOPsForLocation(sourceId);
        if (!cancelled) {
          const rows = data.sops || [];
          setSourceSops(rows);
          setSelected(new Set(rows.map((s) => s.id))); // pre-select all
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load SOPs from that wash');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sourceId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = sourceSops.length > 0 && selected.size === sourceSops.length;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(sourceSops.map((s) => s.id)));
  };

  const runImport = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    setError('');
    try {
      const res = await importSOPs({
        sourceLocationId: Number(sourceId),
        sopIds: Array.from(selected),
      });
      setResult(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Import SOPs from another wash</h2>
          <p className="text-xs text-gray-500 mt-1">
            Copies selected SOPs into this wash. Existing SOPs with the same title and category are skipped so re-importing is safe.
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Copy from</label>
            <select
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {otherLocations.map((l) => (
                <option key={l.id} value={l.id}>{l.business_name || l.display_label || `Location ${l.id}`}</option>
              ))}
            </select>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}

          {result ? (
            <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-900">
              <p className="font-medium">Import complete.</p>
              <p className="mt-1">Imported <strong>{result.imported?.length || 0}</strong> SOP(s).</p>
              {result.skipped && result.skipped.length > 0 && (
                <>
                  <p className="mt-2">Skipped <strong>{result.skipped.length}</strong> that already existed at this wash:</p>
                  <ul className="mt-1 ml-5 list-disc text-xs text-green-800">
                    {result.skipped.map((s, i) => (<li key={i}>{s.title}</li>))}
                  </ul>
                </>
              )}
            </div>
          ) : loading ? (
            <div className="text-center py-8 text-gray-500 text-sm">Loading SOPs…</div>
          ) : sourceSops.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm italic">That wash has no SOPs yet.</div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-700">{selected.size} of {sourceSops.length} selected</span>
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="border border-gray-200 rounded max-h-80 overflow-y-auto divide-y divide-gray-100">
                {sourceSops.map((s) => (
                  <label key={s.id} className="flex items-start gap-2 p-3 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={() => toggle(s.id)}
                      className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">{s.title}</div>
                      <div className="text-xs text-gray-500">{s.category}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={result ? onImported : onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm font-medium"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={runImport}
              disabled={importing || selected.size === 0 || loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded text-sm font-medium"
            >
              {importing ? 'Importing…' : `Import ${selected.size} SOP${selected.size === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceSOPs;
